/**
 * 完整菜單快取新鮮度判斷（純函數，禁止引入 Vuex / API 依賴，單元測試直接 import）
 *
 * 「完整菜單」指 getMenu(keyword: '') 的回應，是 Detail 與購物車比對的查找來源。
 * 同店家在 Cart / Detail / 訂單頁與菜單頁之間往返時，若快照仍新鮮就不重打 API。
 *
 * 新鮮 = 同一家店 + 未逾時 + 期間沒有任何「已知會讓菜單過期」的事件：
 * 後端 MENU 推播、WebSocket 重連（斷線期間的推播不補送）、新一輪購物車衝突。
 * 三者各自有版本號，快照記錄取得當下的值，對不上就代表期間發生過事件。
 */

// 固定五分鐘；命中不延長（不因為使用者一直看菜單就無限期沿用同一份快照）
export const FULL_MENU_CACHE_TTL = 5 * 60 * 1000

/**
 * 取得一次完整菜單請求的版本快照。
 *
 * 呼叫端必須先 commit `store/BUMP_FULL_MENU_REQUEST_SEQ`，並**緊接著同步**呼叫本函數
 * （中間不得有 await），否則併發請求會拿到同一個序號而失去先後判斷。
 *
 * @param {object} params
 * @param {object} params.storeState `store` 模組 state
 * @param {object} params.socketState `socket` 模組 state
 * @param {string|number|null} params.storeId 發出請求時的店家
 * @returns {object} 交給 `store/SET_FULL_MENU_SNAPSHOT` 的版本欄位
 */
export function captureFullMenuRequest({ storeState, socketState, storeId }) {
	return {
		requestSeq: storeState.fullMenuRequestSeq,
		storeId,
		menuChangeSeq: storeState.menuChangeSeq,
		reconnectSeq: socketState.reconnectSeq,
		cartConflictRevision: storeState.cartConflictRevision,
	}
}

/**
 * 本次回應是否真的被套用（沒有被更新的快照擋下）。
 * 被拒絕時代表已有更新的完整快照落地，呼叫端應改用當前 `menus` 收尾。
 */
export function isFullMenuSnapshotApplied(storeState, request) {
	return storeState.appliedFullMenuRequestSeq === request.requestSeq
}

/**
 * @param {object} params
 * @param {Array|null} params.menus 完整菜單快取；成功取得的空菜單 `[]` 也算已載入
 * @param {object|null} params.meta 快照取得當下記錄的 { storeId, fetchedAt, menuChangeSeq, reconnectSeq }
 * @param {string|number|null} params.storeId 當前店家
 * @param {number} params.menuChangeSeq 當前 MENU 推播版本
 * @param {number} params.reconnectSeq 當前 WebSocket 重連版本
 * @param {number} params.cartConflictRevision 當前購物車衝突版本
 * @param {number} params.fullMenuConflictRevision 快照已同步到的購物車衝突版本
 * @param {number} params.now 現在時間（毫秒），由呼叫端傳入以便測試
 * @returns {boolean} 是否可直接沿用快取、略過 API
 */
export function isFullMenuCacheFresh({
	menus,
	meta,
	storeId,
	menuChangeSeq,
	reconnectSeq,
	cartConflictRevision,
	fullMenuConflictRevision,
	now,
}) {
	if (!Array.isArray(menus)) {
		return false
	}
	if (!meta || storeId == null || meta.storeId == null) {
		return false
	}
	if (String(meta.storeId) !== String(storeId)) {
		return false
	}

	// 負數代表系統時間被往回調；快取不持久化，寧可重取也不要無限期沿用
	const age = now - meta.fetchedAt
	if (!(age >= 0 && age < FULL_MENU_CACHE_TTL)) {
		return false
	}

	if (meta.menuChangeSeq !== menuChangeSeq) {
		return false
	}
	if (meta.reconnectSeq !== reconnectSeq) {
		return false
	}
	return fullMenuConflictRevision === cartConflictRevision
}
